import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, Check } from 'lucide-react';
import { useAppStore } from '../store/appStore';
import AppLogo from '../components/EllieAvatar';
import type { AgeGroup } from '../types';

const AVATARS = ['🦁', '🐼', '🦊', '🐸', '🦋', '🦄'];
const AGE_GROUPS: { label: string; value: AgeGroup }[] = [
  { label: '4–6 years', value: '4-6' },
  { label: '7–9 years', value: '7-9' },
  { label: '10–12 years', value: '10-12' },
];

const TOTAL_STEPS = 3;

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction < 0 ? '100%' : '-100%',
    opacity: 0,
  }),
};

const transition = {
  type: 'spring',
  stiffness: 300,
  damping: 30,
};

export default function Onboarding() {
  const navigate = useNavigate();
  const setUser = useAppStore((s) => s.setUser);
  const setActiveChild = useAppStore((s) => s.setActiveChild);

  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);

  // Step 2 state
  const [parentName, setParentName] = useState('');

  // Step 3 state
  const [childName, setChildName] = useState('');
  const [ageGroup, setAgeGroup] = useState<AgeGroup>('7-9');
  const [selectedAvatar, setSelectedAvatar] = useState('🦁');

  const goNext = () => {
    setDirection(1);
    setStep((s) => Math.min(s + 1, TOTAL_STEPS - 1));
  };

  const goBack = () => {
    setDirection(-1);
    setStep((s) => Math.max(s - 1, 0));
  };

  const handleBrowseAsGuest = () => {
    setUser({
      id: 'guest-' + Date.now(),
      email: '',
      name: 'Guest',
      role: 'parent',
      tier: 'guest',
      points: 0,
      earnedBadgeIds: [],
      childProfiles: [],
      savedScenarioIds: [],
      createdBundleIds: [],
      contributionCount: 0,
      createdAt: new Date().toISOString(),
    });
    navigate('/');
  };

  const handleFinish = () => {
    const childId = 'child-' + Date.now();
    const userId = 'user-' + Date.now();

    const childProfile = {
      id: childId,
      userId,
      name: childName.trim() || 'My Child',
      ageGroup,
      avatar: selectedAvatar,
      points: 0,
      streak: 0,
      practicedScenarioIds: [],
      savedScenarioIds: [],
      earnedBadgeIds: [],
    };

    setUser({
      id: userId,
      email: '',
      name: parentName.trim() || 'Parent',
      role: 'parent',
      tier: 'free',
      points: 0,
      earnedBadgeIds: [],
      childProfiles: [childProfile],
      savedScenarioIds: [],
      createdBundleIds: [],
      contributionCount: 0,
      createdAt: new Date().toISOString(),
    });
    setActiveChild(childId);
    const pendingBilling = sessionStorage.getItem('pendingBilling');
    if (pendingBilling) {
      sessionStorage.removeItem('pendingBilling');
      navigate(`/pricing?billing=${pendingBilling}`);
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-100 via-primary-50 to-blue-50 flex flex-col">
      {/* Progress dots */}
      {step > 0 && (
        <div className="flex items-center justify-center gap-2 pt-12 pb-4">
          {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
            <div
              key={i}
              className={`h-2 rounded-full transition-all duration-300 ${
                i === step
                  ? 'w-6 bg-primary-600'
                  : i < step
                  ? 'w-2 bg-primary-400'
                  : 'w-2 bg-gray-200'
              }`}
            />
          ))}
        </div>
      )}

      {/* Slide content */}
      <div className="flex-1 overflow-hidden relative">
        <AnimatePresence custom={direction} mode="wait">
          {step === 0 && (
            <motion.div
              key="step-0"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={transition}
              className="absolute inset-0 flex flex-col items-center justify-center px-8 text-center"
            >
              {/* Logo */}
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                className="mb-6"
              >
                <AppLogo size={120} />
              </motion.div>

              <motion.h1
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.35 }}
                className="font-display text-4xl font-extrabold text-primary-800 leading-tight mb-3"
              >
                KidWise
              </motion.h1>

              <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.45 }}
                className="text-gray-500 text-lg leading-relaxed mb-2"
              >
                Explore. Learn. Grow.
              </motion.p>

              <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.55 }}
                className="text-gray-400 text-sm mb-10"
              >
                Life skills for inspiring young minds
              </motion.p>

              <motion.button
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.65 }}
                whileTap={{ scale: 0.97 }}
                onClick={goNext}
                className="flex items-center gap-2 bg-primary-600 text-white px-10 py-4 rounded-2xl font-bold text-lg shadow-lg shadow-primary-200 hover:bg-primary-700 transition-colors"
              >
                Get Started
                <ChevronRight size={20} />
              </motion.button>

              <motion.button
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.75 }}
                whileTap={{ scale: 0.97 }}
                onClick={handleBrowseAsGuest}
                className="mt-3 text-gray-400 text-sm hover:text-gray-600 transition-colors"
              >
                Browse as guest (3 scenarios)
              </motion.button>

              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.85 }}
                className="mt-4"
              >
                <Link
                  to="/auth"
                  className="inline-flex items-center gap-2 border-2 border-primary-300 text-primary-700 px-8 py-3 rounded-2xl font-semibold text-sm hover:bg-primary-50 transition-colors"
                >
                  Register / Sign In
                </Link>
              </motion.div>
            </motion.div>
          )}

          {step === 1 && (
            <motion.div
              key="step-1"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={transition}
              className="absolute inset-0 flex flex-col items-center justify-center px-6"
            >
              <div className="w-full max-w-md">
                <div className="text-5xl mb-6 text-center">👋</div>
                <h2 className="font-display text-3xl font-extrabold text-primary-800 text-center mb-2">
                  What's your name?
                </h2>
                <p className="text-gray-500 text-center mb-8">
                  Let's personalise your experience
                </p>

                <div className="bg-white rounded-2xl shadow-card p-4 mb-6">
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Parent / Carer name
                  </label>
                  <input
                    type="text"
                    value={parentName}
                    onChange={(e) => setParentName(e.target.value)}
                    placeholder="e.g. Sarah"
                    className="w-full text-xl font-semibold text-gray-800 placeholder-gray-300 outline-none border-none bg-transparent"
                    autoFocus
                    autoComplete="given-name"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={goBack}
                    className="flex items-center gap-1 px-5 py-3 rounded-2xl border border-gray-200 text-gray-500 font-semibold hover:bg-gray-50 transition-colors"
                  >
                    <ChevronLeft size={18} />
                    Back
                  </button>
                  <button
                    onClick={goNext}
                    className="flex-1 flex items-center justify-center gap-2 bg-primary-600 text-white py-3 rounded-2xl font-bold shadow-md hover:bg-primary-700 transition-colors"
                  >
                    Continue
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="step-2"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={transition}
              className="absolute inset-0 flex flex-col items-center justify-center px-6 overflow-y-auto py-6"
            >
              <div className="w-full max-w-md">
                <div className="text-5xl mb-4 text-center">🧒</div>
                <h2 className="font-display text-3xl font-extrabold text-primary-800 text-center mb-1">
                  Add your child
                </h2>
                <p className="text-gray-500 text-center mb-6 text-sm">
                  You can add more children later from your profile
                </p>

                {/* Child name */}
                <div className="bg-white rounded-2xl shadow-card p-4 mb-4">
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Child's name
                  </label>
                  <input
                    type="text"
                    value={childName}
                    onChange={(e) => setChildName(e.target.value)}
                    placeholder="e.g. Lily"
                    className="w-full text-xl font-semibold text-gray-800 placeholder-gray-300 outline-none border-none bg-transparent"
                    autoComplete="off"
                  />
                </div>

                {/* Age group */}
                <div className="bg-white rounded-2xl shadow-card p-4 mb-4">
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                    Age group
                  </label>
                  <div className="flex gap-2">
                    {AGE_GROUPS.map((ag) => (
                      <button
                        key={ag.value}
                        onClick={() => setAgeGroup(ag.value)}
                        className={`flex-1 py-2 rounded-xl text-sm font-semibold border-2 transition-all duration-200 ${
                          ageGroup === ag.value
                            ? 'border-primary-500 bg-primary-50 text-primary-700'
                            : 'border-gray-100 text-gray-500 hover:border-gray-200'
                        }`}
                      >
                        {ag.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Avatar picker */}
                <div className="bg-white rounded-2xl shadow-card p-4 mb-6">
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                    Choose an avatar
                  </label>
                  <div className="grid grid-cols-6 gap-2">
                    {AVATARS.map((emoji) => (
                      <button
                        key={emoji}
                        onClick={() => setSelectedAvatar(emoji)}
                        className={`text-3xl p-2 rounded-xl transition-all duration-200 ${
                          selectedAvatar === emoji
                            ? 'bg-primary-100 scale-110 shadow-sm'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={goBack}
                    className="flex items-center gap-1 px-5 py-3 rounded-2xl border border-gray-200 text-gray-500 font-semibold hover:bg-gray-50 transition-colors"
                  >
                    <ChevronLeft size={18} />
                    Back
                  </button>
                  <button
                    onClick={handleFinish}
                    className="flex-1 flex items-center justify-center gap-2 bg-primary-600 text-white py-3 rounded-2xl font-bold shadow-md hover:bg-primary-700 transition-colors"
                  >
                    <Check size={18} />
                    Let's Go!
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
